import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { listenToUserPayouts } from "../../../../redux/payoutsSlice";
import type { RootState, AppDispatch } from "../../../../redux/store";

const ShortGuestCredits: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);

  const credits = useSelector((state: RootState) => state.payouts.credits);
  const refundCredits = useSelector(
    (state: RootState) => state.payouts.refundCredits
  );

  const totalReverse = useSelector(
    (state: RootState) => state.payouts.totalReverse
  );

  const reverseStatus = useSelector(
    (state: RootState) => state.payouts.reverseStatus
  );

  const [flipped, setFlipped] = useState(false);

  // Listen to payouts/{userId}
  useEffect(() => {
    if (!user?.userId) return;

    const maybeUnsub = dispatch(listenToUserPayouts(user.userId));

    return () => {
      if (typeof maybeUnsub === "function") maybeUnsub();
    };
  }, [user?.userId, dispatch]);

  // SMART SHORT FORMAT
const shortFormat = (n: number | undefined | null) => {
  const value = Number(n) || 0; // ← SAFE DEFAULT

  if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(2) + "B";
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(2) + "M";
  if (value >= 100_000) return Math.round(value / 1000) + "K";
  if (value >= 10_000) return (value / 1000).toFixed(1) + "K";

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};


  const wrapper: React.CSSProperties = {
    perspective: "1400px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "70vh",
  };

  const flipCard: React.CSSProperties = {
    width: "340px",
    height: "260px",
    position: "relative",
    transformStyle: "preserve-3d",
    transition: "transform 0.85s cubic-bezier(0.4, 0.2, 0.2, 1)",
    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
    cursor: "pointer",
  };

  const sideBase: React.CSSProperties = {
    width: "100%",
    height: "100%",
    position: "absolute",
    borderRadius: "20px",
    backfaceVisibility: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  // FRONT = refund credits UI
  const frontOuter: React.CSSProperties = {
    ...sideBase,
    background:
      "linear-gradient(135deg, black, #b8860b, #ffd700, #b8860b, black)",
    padding: "2px",
    boxShadow: "0 10px 35px rgba(0,0,0,0.25)",
  };

  const frontInner: React.CSSProperties = {
    backgroundColor: "#0d0d0d",
    borderRadius: "18px",
    padding: "35px 20px",
    width: "100%",
    height: "100%",
    textAlign: "center",
  };

  const refundTitle: React.CSSProperties = {
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: "#ffd700",
    fontWeight: 600,
    marginBottom: "10px",
  };

  const refundText: React.CSSProperties = {
    fontSize: "3.2rem",
    fontWeight: 800,
    background: "linear-gradient(135deg, #ffd700, #b8860b, #ffd700)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textShadow: "0 3px 6px rgba(0,0,0,0.4)",
    lineHeight: 1,
    display: "flex",
    justifyContent: "center",
  };

  const reverseRow: React.CSSProperties = {
    marginTop: "18px",
    color: "#ffd700",
    fontSize: "0.78rem",
    fontWeight: 500,
  };

  const reverseAmount: React.CSSProperties = {
    fontSize: "1.7rem",
    fontWeight: 700,
    marginTop: "3px",
    background: "linear-gradient(135deg, #ffd700, #b8860b)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const badge: React.CSSProperties = {
    marginTop: "10px",
    display: "inline-block",
    backgroundColor: "#ff6a00",
    color: "white",
    padding: "5px 12px",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "capitalize",
  };

  // BACK = available credits
  const backOuter: React.CSSProperties = {
    ...sideBase,
    transform: "rotateY(180deg)",
    background: "white",
    padding: "2px",
    boxShadow: "0 10px 40px rgba(255,80,20,0.4)",
  };

  const backInner: React.CSSProperties = {
    background: "linear-gradient(135deg, #ff4500, #ff2a00, #d30000)",
    borderRadius: "18px",
    padding: "40px 20px",
    width: "100%",
    height: "100%",
    textAlign: "center",
    color: "white",
  };

  const creditTitle: React.CSSProperties = {
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "10px",
    fontWeight: 600,
  };

  const creditText: React.CSSProperties = {
    fontSize: "3.5rem",
    fontWeight: 800,
    lineHeight: 1,
    display: "flex",
    justifyContent: "center",
    textShadow: "0 3px 8px rgba(0,0,0,0.25)",
  };

  return (
    <div style={wrapper}>
      <div style={flipCard} onClick={() => setFlipped(!flipped)}>
        {/* FRONT — Refund Credits */}
        <div style={frontOuter}>
          <div style={frontInner}>
            <div style={refundTitle}>Refund Credits</div>

            <motion.div
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 150 }}
              style={refundText}
            >
              €{shortFormat(refundCredits)}
            </motion.div>

            {/* NEW — Total Reverse */}
            <div style={reverseRow}>To Be Refunded </div>
            <div style={reverseAmount}>€{shortFormat(totalReverse)}</div>

            {/* NEW — Orange Badge */}
            <div style={badge}>{reverseStatus || "pending"}</div>
          </div>
        </div>

        {/* BACK — Available Credits */}
        <div style={backOuter}>
          <div style={backInner}>
            <div style={creditTitle}>Available Credits</div>
            <motion.div
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 150 }}
              style={creditText}
            >
              €{shortFormat(credits)}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortGuestCredits;
